import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Search, Plus, X, UserPlus, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { LogoMark, Wordmark } from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import ThemeToggle from '../components/ThemeToggle';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { session, profile, refreshProfile } = useAuth();

  const [nama, setNama] = useState('');
  const [emel, setEmel] = useState('');
  const [telefon, setTelefon] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null); // {student_id, nama_topeng, kelas}
  const [links, setLinks] = useState([]); // {student_id, nama_topeng, kelas}
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (session && profile) navigate(`/${profile.role}`, { replace: true });
  }, [session, profile, navigate]);

  const doSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 3) { setResults([]); return; }
    setSearching(true);
    const { data, error } = await supabase.rpc('search_students_masked', { p_query: q.trim() });
    setSearching(false);
    if (error) { setResults([]); return; }
    setResults(data || []);
  }, []);

  useEffect(() => {
    if (picked) return;
    const id = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(id);
  }, [query, picked, doSearch]);

  const addChild = () => {
    if (!picked) return;
    if (links.some((l) => l.student_id === picked.student_id)) { toast.error('Anak sudah ditambah.'); return; }
    setLinks((prev) => [...prev, { ...picked }]);
    setPicked(null); setQuery(''); setResults([]);
    toast.success('Anak ditambah ke senarai.');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password !== password2) { toast.error('Kata laluan tidak sepadan.'); return; }
    if (password.length < 6) { toast.error('Kata laluan minimum 6 aksara.'); return; }
    if (links.length === 0) { toast.error('Sila tautkan sekurang-kurangnya satu anak.'); return; }
    setLoading(true);
    try {
      const { data: su, error: e1 } = await supabase.auth.signUp({ email: emel.trim(), password });
      if (e1) throw e1;
      if (!su.session) {
        const { error: e2 } = await supabase.auth.signInWithPassword({ email: emel.trim(), password });
        if (e2) throw new Error('Sila sahkan emel anda sebelum log masuk.');
      }
      const { error: e3 } = await supabase.rpc('register_parent_links', {
        p_nama: nama.trim(), p_telefon: telefon.trim(),
        p_links: links.map((l) => ({ student_id: l.student_id })),
      });
      if (e3) { throw new Error('Data tidak cocok. Semak nama anak & No. KP.'); }
      await refreshProfile();
      toast.success('Pendaftaran berjaya! Menunggu kelulusan bendahara.');
      navigate('/orangtua', { replace: true });
    } catch (err) {
      toast.error(err.message || 'Data tidak cocok.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="flex items-center justify-between px-6 h-16 bg-card border-b border-border">
        <Link to="/masuk" className="flex items-center gap-2"><LogoMark size={34} /><Wordmark size="text-lg" /></Link>
        <div className="flex items-center gap-2"><ThemeToggle /><LanguageSwitcher /></div>
      </header>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-3xl font-bold text-foreground">{t('daftar')}</h1>
        <p className="mt-2 text-muted-foreground">{t('daftarOrangtuaDesc')}</p>

        <form onSubmit={submit} className="mt-8 space-y-8" data-testid="register-form">
          {/* Akaun */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-5">
            <h2 className="text-lg font-bold text-foreground">{t('maklumatAkaun')}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nama">{t('nama')}</Label>
                <Input id="nama" required value={nama} onChange={(e) => setNama(e.target.value)} className="h-12" data-testid="reg-nama" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="telefon">{t('telefon')}</Label>
                <Input id="telefon" required value={telefon} onChange={(e) => setTelefon(e.target.value)} placeholder="012-3456789" className="h-12" data-testid="reg-telefon" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emel">{t('emel')}</Label>
                <Input id="emel" type="email" required value={emel} onChange={(e) => setEmel(e.target.value)} className="h-12" data-testid="reg-emel" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pw">{t('kataLaluan')}</Label>
                <Input id="pw" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" data-testid="reg-password" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="pw2">{t('sahkanKataLaluan')}</Label>
                <Input id="pw2" type="password" required value={password2} onChange={(e) => setPassword2(e.target.value)} className="h-12" data-testid="reg-password2" />
              </div>
            </div>
          </section>

          {/* Taut anak */}
          <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
            <h2 className="text-lg font-bold text-foreground flex items-center gap-2"><UserPlus className="h-5 w-5 text-emerald2" />{t('tautkanAnak')}</h2>

            {!picked && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('cariNamaAnak')}
                       className="h-12 pl-11" data-testid="reg-search" />
                {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-muted-foreground" />}
                {results.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-xl border border-border bg-card shadow-lg overflow-hidden" data-testid="reg-search-results">
                    {results.map((r) => (
                      <button key={r.student_id} type="button" onClick={() => { setPicked(r); setResults([]); }}
                              className="flex items-center justify-between w-full px-4 py-3 hover:bg-brand-light transition-colors text-left"
                              data-testid={`reg-result-${r.student_id}`}>
                        <span className="font-medium text-foreground">{r.nama_topeng}</span>
                        <span className="text-sm text-muted-foreground">{r.kelas}</span>
                      </button>
                    ))}
                  </div>
                )}
                {query.length > 0 && query.length < 3 && (
                  <p className="mt-1.5 text-xs text-muted-foreground">Taip sekurang-kurangnya 3 huruf.</p>
                )}
              </div>
            )}

            {picked && (
              <div className="rounded-xl border border-brand-border bg-brand-light p-4 space-y-3" data-testid="reg-verify-box">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground">{picked.nama_topeng}</p>
                    <p className="text-sm text-muted-foreground">{picked.kelas} · {picked.sekolah}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button type="button" onClick={addChild} className="h-11 bg-emerald2 hover:bg-emerald2-hover" data-testid="reg-add-child">
                      <Plus className="h-5 w-5 mr-1" />{t('tambah')}
                    </Button>
                    <button type="button" onClick={() => setPicked(null)} className="text-muted-foreground hover:text-muted-foreground" aria-label="batal">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {links.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">{t('anakDitaut')} ({links.length})</p>
                {links.map((l) => (
                  <div key={l.student_id} className="flex items-center justify-between rounded-xl border border-border px-4 py-3" data-testid={`reg-linked-${l.student_id}`}>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald2" />
                      <div>
                        <p className="font-medium text-foreground">{l.nama_topeng}</p>
                        <p className="text-xs text-muted-foreground">{l.kelas}</p>
                      </div>
                    </div>
                    <button type="button" onClick={() => setLinks((p) => p.filter((x) => x.student_id !== l.student_id))} className="text-muted-foreground hover:text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </section>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button type="submit" disabled={loading} className="h-12 flex-1 text-base font-semibold bg-brand text-white hover:bg-brand-royal active:scale-[0.98] transition-transform" data-testid="register-submit">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('daftar')}
            </Button>
          </div>

          <p className="text-sm text-muted-foreground text-center">
            {t('adaAkaun')}{' '}
            <Link to="/masuk" className="font-semibold text-brand hover:underline" data-testid="to-login">{t('logMasukSini')}</Link>
          </p>
          <p className="text-sm text-muted-foreground text-center -mt-4">
            <Link to="/daftar-guru" className="font-semibold text-brand hover:underline" data-testid="to-register-guru">{t('daftarSebagaiGuru')}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
